import { useEffect, useCallback, useRef } from 'react';
import { useChainManager } from './useChainManager';
import { useBlockchainEvent } from './useBlockchainEvents';
import { useSetApprovedMemberMutation } from '@app/services';
import { useToast } from '@chakra-ui/react';
import {
  Event,
  getCampaignEntities,
  selectEvent,
  setEvent,
  setIdle,
} from '@features';
import { useAppDispatch, useAppSelector } from '@hooks';

/**
 * Unified hook for whitelist events across all chains
 * Replaces the old useSetWhitelisterListener that only worked with EVM chains
 */
export function useWhitelistEvents(lixiBagAddress: string) {
  const dispatch = useAppDispatch();
  const { currentChain } = useChainManager();
  const [setApprovedMember] = useSetApprovedMemberMutation();
  const event = useAppSelector(selectEvent);
  const campaignEntities = useAppSelector(state => getCampaignEntities(state));

  const toast = useToast({
    duration: 3000,
    position: 'bottom',
    isClosable: true,
  });

  // Handler for SetWhitelister events
  const handleSetWhitelister = useCallback(
    async (data: any) => {
      console.log("Event 'SetWhitelister' with payload");

      // Extract data based on chain type
      let groupId: number;
      let user: string;
      let isWhitelisted: boolean;

      // Parse groupId safely - handle large numbers
      // Events return string or BN, but we need number for API calls
      
      if (currentChain === 'bnb') {
        // EVM event format - data comes as array from subscribeToEvent
        const rawGroupId = data.data?.arg0?.toNumber?.() || data.data?.arg0 || data.groupId || '0';
        if (typeof rawGroupId === 'number') {
          groupId = rawGroupId;
        } else {
          const parsed = Number(rawGroupId);
          if (isNaN(parsed)) {
            console.error('[useWhitelistEvents] Invalid groupId format:', rawGroupId);
            groupId = 0;
          } else if (parsed > Number.MAX_SAFE_INTEGER) {
            console.warn(
              `[useWhitelistEvents] group_id ${rawGroupId} exceeds Number.MAX_SAFE_INTEGER. ` +
              `Using truncated value (may lose precision).`
            );
            groupId = Number.MAX_SAFE_INTEGER;
          } else {
            groupId = parsed;
          }
        }
        user = data.data?.arg1 || data.user || '';
        isWhitelisted =
          data.data?.arg2 !== undefined
            ? Boolean(data.data?.arg2)
            : data.isWhitelisted !== false;
      } else {
        // Solana or other chains
        const rawGroupId = data.groupId || data.data?.groupId || '0';
        const parsed = Number(rawGroupId);
        if (isNaN(parsed)) {
          console.error('[useWhitelistEvents] Invalid groupId format:', rawGroupId);
          groupId = 0;
        } else if (parsed > Number.MAX_SAFE_INTEGER) {
          console.warn(
            `[useWhitelistEvents] group_id ${rawGroupId} exceeds Number.MAX_SAFE_INTEGER. ` +
            `Using truncated value (may lose precision).`
          );
          groupId = Number.MAX_SAFE_INTEGER;
        } else {
          groupId = parsed;
        }
        user = data.user || data.data?.user || '';
        isWhitelisted = data.isWhitelisted !== false;
      }

      console.log({ groupId, user, isWhitelisted });

      const { eventName, payload } = event;

      if (!payload) {
        return;
      }

      if (
        eventName !== Event.SETWHITELISTER_WAITING &&
        eventName !== Event.SETWHITELISTER_BY_ADMIN_WAITING
      ) {
        return;
      }

      const { campaignId, userId } = payload;

      const campaign = campaignEntities[campaignId];
      if (!campaign || !campaign.members) {
        return;
      }

      const member = campaign.members.find(mb => mb.userId === userId);
      if (!member) {
        return;
      }

      dispatch(
        setEvent({ eventName: Event.SETWHITELISTER_FIRED, payload: null }),
      );

      try {
        const result = await setApprovedMember({
          groupId,
          userAddress: user,
          isWhitelisted,
        });

        console.log(result);

        if ((result as any).error) {
          return toast({
            title: 'Thất bại',
            description: (result as any).error.message,
            status: 'error',
          });
        }

        toast({
          title: 'Duyệt thành công',
          description: 'Bạn đã duyệt thành công',
          status: 'success',
        });
      } catch (error) {
        console.log(error);
        toast({
          title: 'Thất bại',
          description: 'Có lỗi xảy ra khi duyệt',
          status: 'error',
        });
      } finally {
        dispatch(setIdle());
      }
    },
    [event, campaignEntities, dispatch, toast, currentChain],
  );

  // Subscribe to SetWhitelister events
  useBlockchainEvent('SetWhitelister', handleSetWhitelister, {
    contractAddress: lixiBagAddress,
    enabled: !!lixiBagAddress,
  });

  // Also subscribe to SetWhitelisterByAdmin if supported
  useBlockchainEvent('SetWhitelisterByAdmin', handleSetWhitelister, {
    contractAddress: lixiBagAddress,
    enabled: !!lixiBagAddress,
  });

  return {};
}
